import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/db';
import { logger } from '../services/logger';
import { Trophy, Medal, Award, ChevronDown, ChevronUp, Users, FileText, Share2, Copy, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePromoConfig } from '../hooks/usePromoConfig';
import { useAuth } from '../lib/auth';
import { ClickableUserName } from './common/ClickableUserName';
import { colors, ACCENT_COLOR } from '../config/brand';
import { ShareReferralModal } from './ShareReferralModal';

// Gradient style for header
const leaderboardHeaderStyle = {
  background: `linear-gradient(to right, ${colors.secondary.slate}, #7c3aed, #5b21b6)`,
};

interface AffiliateLeaderboardEntry {
  rank_position: number;
  user_id: string;
  user_email: string;
  user_name: string;
  affiliate_count: number | null;
}

interface AffiliateLeaderboardProps {
  limit?: number;
  showDetails?: boolean;
}

export function AffiliateLeaderboard({ limit = 10, showDetails = true }: AffiliateLeaderboardProps) {
  const [entries, setEntries] = useState<AffiliateLeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [userPosition, setUserPosition] = useState<number | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { promoConfig } = usePromoConfig();
  const { user } = useAuth();
  
  // Check if current user is an admin
  const isAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';

  useEffect(() => {
    fetchAffiliateLeaderboard();
    if (user) {
      fetchUserPosition();
      fetchAffiliateCode();
    }
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchAffiliateLeaderboard();
      if (user) fetchUserPosition();
    }, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, user]);

  const fetchAffiliateLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase.rpc('get_affiliate_leaderboard', {
        p_limit: limit,
        p_competition_id: null, // Use active competition
        p_include_count: isAdmin // Only include count for admins
      });

      if (rpcError) throw rpcError;
      setEntries(data || []);
    } catch (err) {
      logger.error('Error fetching affiliate leaderboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPosition = async () => {
    try {
      const { data, error: rpcError } = await supabase.rpc('get_user_competition_positions', {
        p_user_id: user?.id,
        p_competition_id: null
      });

      if (rpcError) throw rpcError;
      if (data && data.length > 0) {
        setUserPosition(data[0].affiliate_position);
      }
    } catch (err) {
      logger.error('Error fetching user position:', err);
    }
  };

  const fetchAffiliateCode = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('users')
        .select('affiliate_code')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setAffiliateCode(data?.affiliate_code || null);
    } catch (err) {
      logger.error('Error fetching affiliate code:', err);
    }
  };

  const getReferralLink = () => {
    return `${window.location.origin}/auth/register?ref=${affiliateCode}`;
  };

  const copyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (affiliateCode) {
      navigator.clipboard.writeText(getReferralLink());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return (
          <div className="relative">
            <Trophy className="w-6 h-6 text-yellow-500 drop-shadow-lg" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
          </div>
        );
      case 2:
        return (
          <div className="relative">
            <Medal className="w-6 h-6 text-gray-400 drop-shadow-md" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-gray-300 rounded-full"></div>
          </div>
        );
      case 3:
        return (
          <div className="relative">
            <Award className="w-6 h-6 text-amber-600 drop-shadow-md" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full"></div>
          </div>
        );
      default:
        return null; // Don't show icon for ranks 4+, just use the # display
    }
  };

  const getRankBadge = (rank: number) => {
    switch (rank) {
      case 1:
        return (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-xs font-bold shadow-lg">
            <Trophy className="w-3.5 h-3.5" />
            <span>1st Place</span>
          </div>
        );
      case 2:
        return (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-gray-300 to-gray-400 text-white text-xs font-bold shadow-md">
            <Medal className="w-3.5 h-3.5" />
            <span>2nd Place</span>
          </div>
        );
      case 3:
        return (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold shadow-md">
            <Award className="w-3.5 h-3.5" />
            <span>3rd Place</span>
          </div>
        );
      default:
        return null;
    }
  };

  const getRankBgColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-300';
      case 2:
        return 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300';
      case 3:
        return 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-300';
      default:
        return 'bg-white border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="text-white p-4" style={leaderboardHeaderStyle}>
          <div className="flex items-center space-x-2">
            <Users className="w-6 h-6" />
            <h2 className="text-xl font-bold">Top Referrers</h2>
          </div>
        </div>
        <div className="flex items-center justify-center h-64 p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: ACCENT_COLOR }}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="text-center text-red-600">
          <p className="text-lg font-medium">Error Loading Leaderboard</p>
          <p className="text-sm mt-2">{error}</p>
          <button
            type="button"
            onClick={fetchAffiliateLeaderboard}
            className="mt-4 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
            style={{ backgroundColor: ACCENT_COLOR }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="text-center text-gray-500">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">No Referrer Data Yet</p>
          <p className="text-sm mt-2">Invite friends to appear on this leaderboard!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header - Clickable to expand/collapse */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-white p-4 text-left hover:opacity-90 transition-opacity"
        style={leaderboardHeaderStyle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Users className="w-6 h-6" />
            <h2 className="text-xl font-bold">Top {limit} Referrers</h2>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-sm font-medium">
              Affiliate Leaderboard
            </div>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </div>
        </div>
        <p className="text-sm mt-2 opacity-90">
          {isAdmin 
            ? 'Users ranked by number of affiliates recruited during this competition'
            : 'Users ranked by referrals during this competition'}
        </p>
      </button>

      {/* User's Own Position Card - Always visible when logged in */}
      {user && !isAdmin && (
        <div className={`px-4 py-3 border-b ${
          userPosition === 1 ? 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200' :
          userPosition === 2 ? 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200' :
          userPosition === 3 ? 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200' :
          'bg-purple-50 border-purple-100'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center space-x-3">
              {userPosition ? (
                <>
                  <span className={`text-sm font-medium ${
                    userPosition <= 3 ? 'text-gray-700' : 'text-purple-700'
                  }`}>
                    Your Position:
                  </span>
                  <div className="flex items-center space-x-2">
                    {userPosition <= 3 && getRankIcon(userPosition)}
                    <span className={`text-lg font-bold ${
                      userPosition === 1 ? 'text-yellow-700' :
                      userPosition === 2 ? 'text-gray-700' :
                      userPosition === 3 ? 'text-amber-700' :
                      'text-purple-700'
                    }`}>
                      #{userPosition}
                    </span>
                    {userPosition <= 3 && (
                      <div className="ml-1">
                        {getRankBadge(userPosition)}
                      </div>
                    )}
                    {userPosition > 3 && userPosition <= 10 && (
                      <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full">
                        Top 10! 🎉
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <span className="text-sm text-purple-700">
                  Invite friends to climb the leaderboard!
                </span>
              )}
            </div>
            {/* Share Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsShareModalOpen(true);
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white text-sm font-medium rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-sm"
            >
              <Share2 className="w-4 h-4" />
              Share & Invite
            </button>
          </div>
        </div>
      )}

      {/* Collapsible Content */}
      {isExpanded && (
        <>
          {/* Leaderboard Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referrer
                  </th>
                  {/* Only show affiliate count column for admins */}
                  {isAdmin && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Affiliates
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {entries.map((entry) => (
                  <tr
                    key={entry.user_id}
                    className={`hover:bg-gray-50 transition-colors ${getRankBgColor(entry.rank_position)}`}
                  >
                    {/* Rank */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-start space-y-1">
                        <div className="flex items-center space-x-2">
                          {getRankIcon(entry.rank_position)}
                          <span className="text-sm font-bold text-gray-700">
                            #{entry.rank_position}
                          </span>
                        </div>
                        {entry.rank_position <= 3 && getRankBadge(entry.rank_position)}
                      </div>
                    </td>

                    {/* Referrer Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white font-medium text-sm"
                          style={{ background: `linear-gradient(to bottom right, #7c3aed, #5b21b6)` }}
                        >
                          {entry.user_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {isAdmin ? (
                              <ClickableUserName userId={entry.user_id} displayName={entry.user_name} />
                            ) : (
                              /* Main dashboard: show first name only, hide last name and email */
                              entry.user_name.trim().split(/\s+/)[0] || entry.user_name
                            )}
                          </div>
                          {showDetails && isAdmin && (
                            <div className="text-xs text-gray-500">
                              {entry.user_email}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Affiliate Count - Only for admins */}
                    {isAdmin && (
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-bold text-purple-600">
                          {entry.affiliate_count}
                        </div>
                        <div className="text-xs text-gray-500">
                          referrals
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Share Your Link Section */}
          {user && !isAdmin && affiliateCode && (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-4 py-4 border-t border-purple-100">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-purple-800 mb-1">
                    🎯 Win R10,000 Cash! Share your referral link:
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={getReferralLink()}
                      className="flex-1 min-w-0 px-3 py-1.5 bg-white border border-purple-200 rounded-lg text-xs font-mono text-gray-600 truncate"
                    />
                    <button
                      type="button"
                      onClick={copyLink}
                      className="flex-shrink-0 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1.5 text-sm"
                    >
                      {copied ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          <span className="hidden xs:inline">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span className="hidden xs:inline">Copy</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsShareModalOpen(true)}
                      className="flex-shrink-0 px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all flex items-center gap-1.5 text-sm shadow-sm"
                    >
                      <Share2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Share</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs text-gray-500">
                Updated every 30 seconds • Only counts referrals during competition
                {promoConfig?.start_date && (
                  <span> • Started: {new Date(promoConfig.start_date).toLocaleDateString()}</span>
                )}
              </p>
              {promoConfig?.competition_terms_content && (
                <Link 
                  to="/competition-terms" 
                  className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                >
                  <FileText className="w-3 h-3" />
                  View T&Cs
                </Link>
              )}
            </div>
          </div>
        </>
      )}

      {/* Share Referral Modal */}
      <ShareReferralModal 
        isOpen={isShareModalOpen} 
        onClose={() => setIsShareModalOpen(false)} 
      />
    </div>
  );
}

export default AffiliateLeaderboard;

